// pages/lab/methodology.js
// ════════════════════════════════════════════════════════════════════
// LAB — Methodology page (Phase F11)
//
// Public-facing explainer: what is a Lab finding, what are the 5
// narrative forms, what do lifecycle / visibility / citation handle
// mean, what is pre-registration discipline.
//
// This is the orientation page a reader needs before landing on a
// finding — currently they hit a finding cold and don't know what
// "Form 4" or "lifecycle: pre-registered" mean.
// ════════════════════════════════════════════════════════════════════

import Head from 'next/head';
import Link from 'next/link';

export default function MethodologyPage() {
  return (
    <div className="sp-root">
      <Head>
        <title>Methodology · Lab · Shift</title>
        <meta
          name="description"
          content="What a Lab finding is, the 5 narrative forms, lifecycle states, visibility model, citation handle format, and pre-registration discipline."
        />
      </Head>

      <div className="sp-page">
        <div className="findings-index-topbar">
          <Link href="/findings" className="finding-back">← Findings index</Link>
          <div className="findings-index-topbar-meta">
            Lab · Methodology
          </div>
        </div>

        <header className="findings-index-header">
          <div className="finding-eyebrow">§ How Lab works</div>
          <h1 className="findings-index-title">Methodology.</h1>
          <p className="findings-index-sub">
            What a Lab finding is, the discipline behind the corpus, and
            how to read the labels on every finding card. Read this once;
            the conventions are stable across every finding in the corpus.
          </p>
        </header>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ What is a Lab finding</div>
          <h2 className="methodology-h2">A finding is a structured research artifact, not an essay.</h2>
          <p className="methodology-prose">
            A Lab finding is a citable claim with: a load-bearing thesis, a corpus of evidence (cases, data, source documents), a mechanism explanation, an honest exposure of how it could be wrong, and at least one pre-registered prediction with a public falsifier. Findings are versioned, lifecycle-tracked, and citable by handle. They are the unit of intellectual output the Lab produces.
          </p>
          <p className="methodology-prose">
            Findings are <strong>not</strong> essays, opinions, or summaries of others’ work. They are claims the Lab is willing to be wrong about, structured so that being wrong is visible.
          </p>
        </section>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ Findings vs Briefs vs Tectonic Shifts — three artifact types</div>
          <h2 className="methodology-h2">Three artifacts, three cadences, three epistemic statuses.</h2>
          <p className="methodology-prose">
            The Lab produces three distinct artifact types. They serve different functions and should not be confused with each other.
          </p>
          <div className="methodology-pill-grid">
            <PillRow tone="brass" label="Lab Finding">
              The load-bearing claim. Pre-registered, falsifiable, version-pinned, citable. Authored over weeks. Cadence: monthly+. Lives at <code className="methodology-inline-code">/findings</code>.
            </PillRow>
            <PillRow tone="sage" label="Weekly Brief">
              Cross-corpus weekly synthesis. NOT a finding — no falsifier, no cohort-completion resolution. Cites the underlying findings + shifts. Cadence: weekly. Lives at <Link href="/lab/briefs" className="methodology-inline-link">/lab/briefs</Link>.
            </PillRow>
            <PillRow tone="cognac" label="Tectonic Shift">
              Macro frame, not a tested claim. Multi-year structural force tracked through stages, beneficiaries, watch metrics. Cites findings that test its mechanism. Cadence: per-shift updates ~6-8 weeks. Lives at <Link href="/strategic-playbook/tectonic-shifts" className="methodology-inline-link">/strategic-playbook/tectonic-shifts</Link>.
            </PillRow>
          </div>
          <p className="methodology-prose">
            <strong>The distinction matters because epistemic status matters.</strong> Citing a brief as if it were a finding inflates its claim weight; citing a finding as if it were a brief deflates it. Each artifact carries the discipline appropriate to its cadence and form.
          </p>
        </section>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ The five narrative forms</div>
          <h2 className="methodology-h2">Different claim shapes need different scaffolds.</h2>
          <p className="methodology-prose">
            Every finding belongs to one of five narrative forms. The form determines the section structure — what the finding leads with, how the evidence is laid out, what the mechanism explanation looks like. Matching form to claim is itself a methodological discipline; a counterintuitive surprise is told differently than a paired-case comparison.
          </p>

          <div className="methodology-form-grid">
            <FormCard
              num={1}
              name="Detective story"
              purpose="Counterintuitive findings; surprises that overturn conventional wisdom."
              sections="Puzzle → Suspects → Investigation → Reveal → Implications → How wrong → Watching"
              example="(none yet — open slot in the corpus)"
            />
            <FormCard
              num={2}
              name="Pair comparison"
              purpose="Most-similar / most-different paired-case findings."
              sections="Two cases → Split → Wedge → Mechanism → Generalizes → How wrong → Implies → Watching"
              example="Korea vs Hungary — what pronatalist spending can buy"
              exampleHref="/findings/popdec/2026/korea-hungary-divergence"
            />
            <FormCard
              num={3}
              name="Falsification test"
              purpose="Outcomes that resolve a pre-registered prediction. Or — for forward-looking work — predictions awaiting resolution."
              sections="Hypothesis → Prediction → Happened → Verdict → What next → Accounting → Watching"
              example="Population Decline — positive-case search"
              exampleHref="/findings/popdec/2026/positive-case-search"
            />
            <FormCard
              num={4}
              name="Pattern across cases"
              purpose="Findings that emerge across 3+ analogs with a common mechanism."
              sections="Pattern → Cases → Exceptions → Mechanism → Boundary → How wrong → Implies → Watching"
              example="The Asymmetric Underdog Pattern"
              exampleHref="/findings/strategic/2026/asymmetric-underdog-inversion"
            />
            <FormCard
              num={5}
              name="Counterfactual walk"
              purpose="Trajectory-tracing findings, deviation-point analyses."
              sections="Observed → Branches → Counterfactuals → Levers → What it tells → How wrong → Implies → Watching"
              example="Korea’s Lock-In — counterfactual walk"
              exampleHref="/findings/popdec/2026/korea-lockin"
            />
          </div>
        </section>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ Lifecycle states</div>
          <h2 className="methodology-h2">Where a finding sits in its arc.</h2>
          <p className="methodology-prose">
            Every finding carries one of five lifecycle states. The state determines what the reader can expect from the claim and from the version history.
          </p>
          <div className="methodology-pill-grid">
            <PillRow tone="neutral" label="Draft">
              Authored but not yet committed. Bands and predictions may shift. Not citable as a final claim.
            </PillRow>
            <PillRow tone="brass" label="Pre-registered">
              Committed before resolution. Predictions are public, bands are committed, falsifiers are stated. Citable as a research commitment, not as a resolved claim.
            </PillRow>
            <PillRow tone="sage" label="Published">
              Resolution data has arrived. Verdict is held. The claim is the published reading of the evidence; predictions resolved as forecast.
            </PillRow>
            <PillRow tone="cognac" label="Revised (superseded)">
              A newer version exists at the same slug. Original stays at its versioned URL with a "what changed" banner pointing forward. Cite the version, not the slug.
            </PillRow>
            <PillRow tone="oxblood" label="Retracted">
              Resolution overturned the original claim. Article still renders so the retraction is visible; the retraction note explains what failed and why.
            </PillRow>
          </div>
        </section>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ Visibility</div>
          <h2 className="methodology-h2">Who can see what, and how the Lab grows.</h2>
          <p className="methodology-prose">
            Visibility is set per finding, separate from lifecycle. A pre-registered finding can be private (still maturing) or public-peek (open to scrutiny but not collaboration). The model is designed so that drafts can mature in private without the social pressure of an audience, but published findings carry their visibility on the page.
          </p>
          <div className="methodology-pill-grid">
            <PillRow tone="neutral" label="Private">
              Visible only to the author. Used while drafting; not citable from outside.
            </PillRow>
            <PillRow tone="brass" label="Shared by link">
              Visible to anyone with the URL. Used for review by a small invited circle before publication.
            </PillRow>
            <PillRow tone="sage" label="Public · read-only">
              Listed in the corpus, indexed in sitemap, citable. The default for every published Lab finding.
            </PillRow>
            <PillRow tone="plum" label="Public · collab">
              Public + accepts replications, branches, and structured critiques. (Future state — collaboration mechanics not yet wired.)
            </PillRow>
          </div>
        </section>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ Citation handles</div>
          <h2 className="methodology-h2">Stable, version-pinned, machine-parseable.</h2>
          <p className="methodology-prose">
            Every finding has a canonical citation handle of the form:
          </p>
          <pre className="methodology-codeblock"><code>lab:finding/&lt;domain&gt;/&lt;year&gt;/&lt;slug&gt;/&lt;version&gt;</code></pre>
          <p className="methodology-prose">
            For example: <code className="methodology-inline-code">lab:finding/popdec/2026/korea-hungary-divergence/v1</code>. The handle resolves to a URL of the same shape under <code className="methodology-inline-code">/findings/</code>, so cross-references in finding bodies are clickable. Bare-slug URLs (without the version) resolve to the latest version with a <code className="methodology-inline-code">&lt;link rel=&quot;canonical&quot;&gt;</code> pointing to the versioned URL — so external citations to a bare slug stay stable while internal navigation always reaches the current reading.
          </p>
          <p className="methodology-prose">
            Versioning is strict. v2 of a finding is a new artifact, not a quiet edit of v1. The original v1 stays at its versioned URL, lifecycle moves to <code className="methodology-inline-code">revised</code>, and a "what changed" banner on v2 explains the delta. <strong>Citations from outside the Lab should always reference the version</strong>, not the bare slug, so the cited reading is preserved exactly as it was at citation time.
          </p>
        </section>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ Pre-registration discipline</div>
          <h2 className="methodology-h2">The bands are committed before the data arrives.</h2>
          <p className="methodology-prose">
            Pre-registration is the load-bearing methodological move that separates Lab findings from written opinions. Every finding includes at least one prediction with: a central estimate, a predicted band, a falsifier band (the threshold past which the finding is wrong), and a resolution date. All four are committed before resolution data is available.
          </p>
          <p className="methodology-prose">
            The full register lives at the <Link href="/lab/predictions" className="methodology-inline-link">pre-registration calendar</Link>, sorted by resolution date. When a prediction’s resolution date arrives, the parent finding’s lifecycle moves accordingly:
          </p>
          <ul className="finding-bullets">
            <li><strong>Held</strong> — verdict matches the predicted band; finding moves to <code className="methodology-inline-code">published</code>, predictions resolved as forecast.</li>
            <li><strong>Overturned</strong> — verdict outside the falsifier band; finding moves to <code className="methodology-inline-code">retracted</code>; retraction note explains what failed.</li>
            <li><strong>Partial</strong> — verdict between predicted band and falsifier band, or wedge attribution contested; finding moves to <code className="methodology-inline-code">revised</code>; v2 narrows the original claim to the verified subset.</li>
          </ul>
        </section>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ Anti-goalpost-moving commitments</div>
          <h2 className="methodology-h2">What we promise NOT to do at resolution.</h2>
          <p className="methodology-prose">
            The most common failure mode of falsification claims in social science is moving the goalposts at resolution — quietly redefining the test once the data is in. Every Lab finding with a forward-looking prediction includes pre-registered <em>anti-goalpost commitments</em>: the specific saves we will refuse to allow ourselves at resolution.
          </p>
          <p className="methodology-prose">
            These commitments are made <em>now</em>, while we still don’t know the answer, so that future-us cannot save the finding by quietly shifting what counts as confirmation. They typically include: rejecting partial-data exemptions, refusing to expand the candidate set after resolution, requiring independent attribution of mechanism, and binding ourselves to the specific bar value committed at v1 even if a "more lenient" reading would let the finding survive.
          </p>
          <p className="methodology-prose">
            Read the <FindingCiteLink slug="positive-case-search" domain="popdec" year={2026} version="v1">positive-case-search</FindingCiteLink> finding for a worked example of how this is structured in practice.
          </p>
        </section>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ The reflexivity tag</div>
          <h2 className="methodology-h2">When the finding affects the system being studied.</h2>
          <p className="methodology-prose">
            Some findings, if widely cited, can change the systems they describe. A finding about market structure changes how traders behave; a finding about fertility trajectories affects panic, capital flight, and individual decisions. The reflexivity tag (LOW / MEDIUM / HIGH) flags this on every finding so readers can calibrate.
          </p>
          <p className="methodology-prose">
            HIGH reflexivity findings are framed as diagnostic + a menu of tested-intervention positions, not as prescriptive policy advice. The framing matters: a finding that says "policy X cannot durably lift fertility" is research; the same finding rewritten as "you should defund family policy" is advocacy, and weaponizes the research against the institutional support it depends on.
          </p>
        </section>

        <section className="methodology-section">
          <div className="finding-eyebrow">§ The five narrative forms — when to use which</div>
          <h2 className="methodology-h2">A short decision tree.</h2>
          <ul className="finding-bullets">
            <li>Surprised by what you found? <strong>Form 1 (Detective story).</strong></li>
            <li>Two cases, one pair, divergent outcomes? <strong>Form 2 (Pair comparison).</strong></li>
            <li>Resolving a registered prediction (or registering one for the future)? <strong>Form 3 (Falsification test).</strong></li>
            <li>Pattern across 3+ analogs? <strong>Form 4 (Pattern across cases).</strong></li>
            <li>One trajectory, multiple branch points, counterfactual analysis? <strong>Form 5 (Counterfactual walk).</strong></li>
          </ul>
          <p className="methodology-prose">
            Mismatches show up in the writing — a counterintuitive finding crammed into the pair-comparison scaffold reads as flat; a paired-case finding squeezed into the detective scaffold buries the wedge. Match the scaffold to the claim before drafting.
          </p>
        </section>

        <footer className="findings-index-footer">
          <p>
            The Lab corpus index is at <Link href="/findings" className="methodology-inline-link">/findings</Link>. The pre-registration calendar is at <Link href="/lab/predictions" className="methodology-inline-link">/lab/predictions</Link>. Methodology is locked at v2.0 — incremental updates may sharpen the labels but the underlying conventions are stable.
          </p>
        </footer>
      </div>
    </div>
  );
}

function FormCard({ num, name, purpose, sections, example, exampleHref }) {
  return (
    <div className="methodology-form-card">
      <div className="methodology-form-num">Form {num}</div>
      <h3 className="methodology-form-name">{name}</h3>
      <p className="methodology-form-purpose">{purpose}</p>
      <div className="methodology-form-sections">
        <div className="methodology-form-eyebrow">Section flow</div>
        <div>{sections}</div>
      </div>
      <div className="methodology-form-example">
        <div className="methodology-form-eyebrow">Example</div>
        {exampleHref ? (
          <Link href={exampleHref} className="methodology-inline-link">{example}</Link>
        ) : (
          <span className="methodology-form-example-empty">{example}</span>
        )}
      </div>
    </div>
  );
}

function PillRow({ tone, label, children }) {
  return (
    <div className="methodology-pill-row">
      <span className={`finding-version-badge finding-version-badge-${tone} methodology-pill-badge`}>
        {label}
      </span>
      <span className="methodology-pill-desc">{children}</span>
    </div>
  );
}

function FindingCiteLink({ domain, year, slug, version, children }) {
  const href = version
    ? `/findings/${domain}/${year}/${slug}/${version}`
    : `/findings/${domain}/${year}/${slug}`;
  return <Link href={href} className="methodology-inline-link">{children}</Link>;
}
